import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
	CompanyLists,
	IUser,
	IUserAsset,
	IUserCompany,
	LocalizedTextIds,
	MatchedCompanyInfo,
} from 'company-finder-common';
import { DeploymentContext, ServiceBase } from '@Common';
import { Logger } from '../../utilities/logger/logger.js';
import { AuthnService } from '../authn/authn.service.js';
import { WebAnalyticsService } from '../web-analytics/web.analytics.js';
import { of } from 'rxjs';

interface DeferredPromiseHandler<T> {
	promise: Promise<T>;
	resolve: (value?: IUser | PromiseLike<IUser>) => void;
	reject: (reason?: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class UserService extends ServiceBase {
	private requestsForUser: DeferredPromiseHandler<IUser>[] = [];
	protected _logger = new Logger(this.constructor.name);
	private static getUserErrored = false;
	private singleJuniverseAuthAttempted = false;

	constructor(
		_httpClient: HttpClient,
		_context: DeploymentContext,
		protected authnService: AuthnService,
		protected webAnalyticsService: WebAnalyticsService
	) {
		super(_httpClient, _context, '/user');
	}

	// Keep a local cache of the last fetched/updated user settings
	private currentCachedUser: IUser;

	public async getCurrentUser(): Promise<IUser> {
		// NOTE: We used to provide an option to explicitly request the user's information be updated from LDAP.
		//       but that was complicated by the caching mechanism in place, and wasn't buying us much anyway,
		//       so as of ADJQ-838 that option has been removed.
		try {
			if (UserService.getUserErrored) {
				return null;
			}

			this.currentCachedUser = await this.get<IUser>(this._apiUrl);
		} catch (ex) {
			UserService.getUserErrored = true;
			this._logger.warn(`Error loading user to update cache. ${ex}`);
			throw ex;
		}

		return this.currentCachedUser;
	}

	public clearCachedUser(): void {
		this.currentCachedUser = null;
	}

	/**
	 * This will only ever make 1 attempt at Juniverse Auth
	 */
	public async singleAttemptAtJuniverseAuth(): Promise<boolean> {
		// If we don't short circuit, user without Juniverse creds would trigger this at high frequency.
		// This relies on UserService being a singleton Angular Provider.
		if (!this.singleJuniverseAuthAttempted) {
			// To be extra safe, set this before the async call to ensure we don't end up with multiple concurrent async requests
			this.singleJuniverseAuthAttempted = true;
			// If we are in Juniverse, we need to call to get a JWT instead of the cookie-based authentication.
			return await this.authnService.attemptJuniverseAuth(
				this.webAnalyticsService,
				this._context.autoSendBypass,
				this._context.tokenToSend
			);
		}
	}

	public async getCachedUser(): Promise<IUser> {
		// Fetch if not cached yet.
		// FUTURE Put in cache aging to expire cached value after something like a minute
		if (!this.authnService.isAuthenticated) {
			return null;
		}

		if (this.currentCachedUser == null) {
			// Only send the first request, queue up the others as deferred objects we'll resolve when the first request resolves.
			const sendRequest = this.requestsForUser?.length === 0;

			const deferred: DeferredPromiseHandler<IUser> = {
				promise: null,
				resolve: null,
				reject: null,
			};
			deferred.promise = new Promise<IUser>((resolve, reject) => {
				deferred.resolve = resolve;
				deferred.reject = reject;
			});

			this.requestsForUser.push(deferred);

			if (sendRequest) {
				// Wait for the actual API request.
				await this.getCurrentUser();
				// Now that we have a user, resolve the deferred objects.
				while (this.requestsForUser.length > 0) {
					const dph = this.requestsForUser.shift();
					dph.resolve(this.currentCachedUser);
				}
			} else {
				// Calls after the first get deferred, and will wait for their promises to get resolved.
				return deferred.promise;
			}
		}

		return this.currentCachedUser;
	}

	// Assuming we are properly using the user resolver, this will be populated already
	// and we can avoid an async setting up components. Without being behind the resolver,
	// there is no gaurentee that the get user call has run and these could be null
	public getCachedUserSync(): IUser {
		return this.currentCachedUser;
	}

	public getCompanyListsSync(): CompanyLists {
		const list = this.getCachedUserSync()?.companyLists;
		return new CompanyLists(
			list?.isCompanyContactFor,
			list?.isSiteHeadFor,
			list?.isCoOwnerFor
		);
	}

	public async getCompanyLists(): Promise<CompanyLists> {
		const list = (await this.getCachedUser())?.companyLists;
		return new CompanyLists(
			list?.isCompanyContactFor,
			list?.isSiteHeadFor,
			list?.isCoOwnerFor
		);
	}

	public get isReviewer(): boolean {
		return (
			this.getCompanyListsSync()?.isSiteHeadFor?.length > 0 ||
			this.getCompanyListsSync()?.isCoOwnerFor?.length > 0
		);
	}

	public get companiesForReview(): string[] {
		return [
			// Avoid duplicating companies in this list, for a lighter weight server request
			...new Set([
				...(this.getCompanyListsSync()?.isSiteHeadFor ?? []),
				...(this.getCompanyListsSync()?.isCoOwnerFor ?? []),
			]),
		];
	}

	public get companiesForUpdate(): string[] {
		return this.getCompanyListsSync()?.isCompanyContactFor ?? [];
	}

	public get isCompanyContact(): boolean {
		return this.getCompanyListsSync()?.isCompanyContactFor?.length > 0;
	}

	public get hasCompaniesForUpdate(): boolean {
		return this.isReviewer || this.isCompanyContact;
	}

	public async save(user: IUser): Promise<IUser> {
		const result = await this.post<IUser>(
			this._apiUrl,
			JSON.stringify(user)
		);

		if (!this.currentCachedUser) {
			return result;
		}
		// If we have read the User from db at least once, update any fields that are being updated
		if (result.assetsFollowed != null) {
			this.currentCachedUser.assetsFollowed = result.assetsFollowed;
		}
		if (result.companiesFollowed != null) {
			this.currentCachedUser.companiesFollowed = result.companiesFollowed;
		}
		if (result.sectorsFollowed != null) {
			this.currentCachedUser.sectorsFollowed = result.sectorsFollowed;
		}
		if (result.locationsFollowed != null) {
			this.currentCachedUser.locationsFollowed = result.locationsFollowed;
		}
		if (result.userSearches != null) {
			// Assume all searches are being updated at once.
			// If a single search is updated, it will be through a different method
			this.currentCachedUser.userSearches = result.userSearches;
		}
		if (result.followCompaniesImResponsibleFor != null) {
			this.currentCachedUser.followCompaniesImResponsibleFor =
				result.followCompaniesImResponsibleFor;
		}
		return result;
	}

	public async saveCompaniesFollowed(
		companiesFollowed: IUserCompany[]
	): Promise<IUser> {
		const user = await this.getCachedUser();
		const newUser: IUser = this.cloneBaseUser(user);
		newUser.companiesFollowed = companiesFollowed;

		return this.save(newUser);
	}

	public async saveAssetsFollowed(
		assetsFollowed: IUserAsset[]
	): Promise<IUser> {
		const user = await this.getCachedUser();
		const newUser: IUser = this.cloneBaseUser(user);
		newUser.assetsFollowed = assetsFollowed;

		return this.save(newUser);
	}

	public async isFollowingCompany(companyId: string): Promise<boolean> {
		const user = await this.getCachedUser();
		return (
			user?.companiesFollowed?.find(
				(userCompany) => userCompany.companyId === companyId
			) != null
		);
	}

	public async setFollowingCompanies(
		companyIds: string[],
		shouldBeFollowing: boolean
	): Promise<void> {
		const user = await this.getCachedUser();
		const companiesFollowed = user?.companiesFollowed || [];

		for (const companyId of companyIds) {
			const companyPos = companiesFollowed.findIndex(
				(userCompany) => userCompany.companyId === companyId
			);

			const isFollowing = companyPos >= 0;

			if (isFollowing !== shouldBeFollowing) {
				if (shouldBeFollowing) {
					companiesFollowed.push({ companyId: companyId });
				} else {
					companiesFollowed.splice(companyPos, 1);
				}
			}
		}
		this.saveCompaniesFollowed(companiesFollowed);
	}

	public async setFollowingAssets(
		assetIds: string[],
		shouldBeFollowing: boolean
	): Promise<IUser> {
		const user = await this.getCachedUser();
		const assetsFollowed = user?.assetsFollowed || [];

		for (const assetId of assetIds) {
			const assetPos = assetsFollowed.findIndex(
				(userAsset) => userAsset.assetId === assetId
			);

			const isFollowing = assetPos >= 0;

			if (isFollowing !== shouldBeFollowing) {
				if (shouldBeFollowing) {
					assetsFollowed.push({ assetId: assetId });
				} else {
					assetsFollowed.splice(assetPos, 1);
				}
			}
		}
		return this.saveAssetsFollowed(assetsFollowed);
	}

	public async setFollowingCompany(
		companyId: string,
		shouldBeFollowing: boolean
	): Promise<void> {
		this.setFollowingCompanies([companyId], shouldBeFollowing);
	}

	public async setFollowingAsset(
		assetId: string,
		shouldBeFollowing: boolean
	): Promise<IUser> {
		return this.setFollowingAssets([assetId], shouldBeFollowing);
	}

	private cloneBaseUser(user: IUser): IUser {
		if (!user) {
			return null;
		}

		return {
			updatedDate: new Date(),
			...user,
		};
	}

	public async getMatchedCompanies(): Promise<MatchedCompanyInfo[]> {
		return this.get<MatchedCompanyInfo[]>(
			`${this._apiUrl}/matchedCompanies`
		);
	}

	public async getMatchedCompaniesForWizard(
		user: IUser
	): Promise<MatchedCompanyInfo[]> {
		return this.post<MatchedCompanyInfo[]>(
			`${this._apiUrl}/matchedCompanies/wizard`,
			JSON.stringify(user)
		);
	}

	public async sendMagicLink(email: string, encodedTargetUrl: string) {
		try {
			const resp = await this.get<{ message: string }>(
				`${this._apiUrl}/magicLink?email=${encodeURIComponent(email)}&targetUrl=${encodedTargetUrl}`,
				{
					skipURIEncoding: true,
					handleError: (error: HttpErrorResponse) => {
						if (error.status === 429) {
							return of<{ message: string }>({
								message:
									error?.error?.message ??
									this._context.Loc(
										LocalizedTextIds.JNJErrorHandlerSomethingUnexpected
									),
							});
						}

						return of<{ message: string }>({
							message: this._context.Loc(
								LocalizedTextIds.JNJErrorHandlerSomethingUnexpected
							),
						});
					},
				}
			);
			return resp.message;
		} catch (error) {
			console.log(error);
		}
	}
}
